import { Component } from "@theme/component";
import { onAnimationEnd } from "@theme/utilities";
import { DialogOpenEvent, DialogCloseEvent } from "@theme/dialog";

/**
 * A custom element that manages the search dropdown overlay.
 * Mirrors the DialogComponent API by dispatching DialogOpenEvent/DialogCloseEvent
 * so PredictiveSearchComponent works unchanged inside it.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overlay - The overlay container element.
 * @property {HTMLElement} backdrop - The backdrop element.
 *
 * @extends {Component<Refs>}
 */
class SearchDropdownComponent extends Component {
  requiredRefs = ["overlay", "backdrop"];

  #previousScrollY = 0;

  /** @type {boolean} */
  #isOpen = false;

  /** @type {HTMLElement[]} */
  #headerMenuHoverTargets = [];

  /** @type {string | null} */
  #previousTransparentValue = null;

  /** @type {boolean} */
  #removedTransparentAttribute = false;

  #setHeaderTransparencyOverride = (/** @type {boolean} */ isOpen) => {
    const header = document.querySelector("#header-component");
    if (!(header instanceof HTMLElement)) return;

    if (isOpen) {
      if (!header.hasAttribute("transparent")) return;
      this.#previousTransparentValue = header.getAttribute("transparent");
      this.#removedTransparentAttribute = true;
      header.removeAttribute("transparent");
    } else {
      if (!this.#removedTransparentAttribute) return;

      header.setAttribute("transparent", this.#previousTransparentValue ?? "");
      this.#removedTransparentAttribute = false;
      this.#previousTransparentValue = null;
    }
  };

  #handleHeaderMenuHover = () => {
    if (!this.#isOpen) return;
    if (this.refs.overlay.classList.contains("search-dropdown--closing"))
      return;
    this.closeDropdown();
  };

  #attachHeaderMenuHoverListeners = () => {
    this.#detachHeaderMenuHoverListeners();

    if (!window.matchMedia("(hover: hover)").matches) return;

    const header = document.querySelector("#header-component");
    if (!(header instanceof HTMLElement)) return;

    const selector =
      ".header-menu .menu-list__list-item:where(:not([slot='overflow'])) > .menu-list__link, " +
      ".header-menu .menu-list__list-item:where(:not([slot='overflow'])) > button.menu-list__link";

    const targets = Array.from(header.querySelectorAll(selector)).filter(
      (target) => target instanceof HTMLElement,
    );

    this.#headerMenuHoverTargets = /** @type {HTMLElement[]} */ (targets);

    for (const target of this.#headerMenuHoverTargets) {
      target.addEventListener("mouseenter", this.#handleHeaderMenuHover);
    }
  };

  #detachHeaderMenuHoverListeners = () => {
    for (const target of this.#headerMenuHoverTargets) {
      target.removeEventListener("mouseenter", this.#handleHeaderMenuHover);
    }
    this.#headerMenuHoverTargets = [];
  };

  /**
   * Keeps the dropdown anchored directly below the current header group height.
   */
  #syncOverlayOffset = () => {
    const headerGroup = document.querySelector("#header-group");
    const header = document.querySelector("#header-component");

    const getBottom = (/** @type {Element | null} */ element) => {
      if (!(element instanceof HTMLElement)) return 0;
      return Math.round(element.getBoundingClientRect().bottom);
    };

    const announcementBottom = Array.from(
      document.querySelectorAll(
        "announcement-bar-component, announcement-bar, .announcement-bar, .announcement-bar-section",
      ),
    ).reduce((maxBottom, element) => {
      return Math.max(maxBottom, getBottom(element));
    }, 0);

    const topOffset = Math.max(
      0,
      getBottom(headerGroup),
      getBottom(header),
      announcementBottom,
    );

    this.refs.overlay.style.setProperty(
      "--search-dropdown-top-offset",
      `${topOffset}px`,
    );
  };

  #handleViewportResize = () => {
    if (!this.#isOpen) return;
    this.#syncOverlayOffset();
  };

  connectedCallback() {
    super.connectedCallback();
    this.refs.backdrop.addEventListener("click", this.closeDropdown);
    window.addEventListener("resize", this.#handleViewportResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.backdrop.removeEventListener("click", this.closeDropdown);
    document.removeEventListener("keydown", this.#handleKeyDown);
    window.removeEventListener("resize", this.#handleViewportResize);
    this.#detachHeaderMenuHoverListeners();
    this.#setHeaderTransparencyOverride(false);
  }

  get isOpen() {
    return this.#isOpen;
  }

  showDropdown() {
    const { overlay } = this.refs;
    if (this.#isOpen) return;
    this.#isOpen = true;

    this.#previousScrollY = window.scrollY;

    requestAnimationFrame(() => {
      document.body.style.overflow = "hidden";
      this.#setHeaderTransparencyOverride(true);
      this.#attachHeaderMenuHoverListeners();

      this.#syncOverlayOffset();

      overlay.hidden = false;
      overlay.classList.remove("search-dropdown--closing");
      overlay.classList.add("search-dropdown--open");

      document.addEventListener("keydown", this.#handleKeyDown);

      this.dispatchEvent(new DialogOpenEvent());
    });
  }

  closeDropdown = async () => {
    const { overlay } = this.refs;
    if (!this.#isOpen) return;

    document.removeEventListener("keydown", this.#handleKeyDown);

    overlay.classList.remove("search-dropdown--open");
    overlay.classList.add("search-dropdown--closing");

    const panel = overlay.querySelector(".search-dropdown__panel");
    if (panel) {
      await onAnimationEnd(panel, undefined, { subtree: false });
    }

    document.body.style.overflow = "";
    this.#detachHeaderMenuHoverListeners();
    this.#setHeaderTransparencyOverride(false);

    overlay.classList.remove("search-dropdown--closing");
    overlay.hidden = true;
    this.#isOpen = false;

    this.dispatchEvent(new DialogCloseEvent());
  };

  toggleDropdown = () => {
    if (this.#isOpen) {
      this.closeDropdown();
    } else {
      this.showDropdown();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key !== "Escape") return;
    event.preventDefault();
    this.closeDropdown();
  };
}

if (!customElements.get("search-dropdown-component")) {
  customElements.define("search-dropdown-component", SearchDropdownComponent);
}

export { SearchDropdownComponent };
